/**
 * TopnoteWS — WebSocket client with JWT auth and exponential backoff reconnect.
 *
 * Connects to /api/v1/ws?token=<jwt>. Server sends heartbeat every 30s;
 * if no heartbeat for 60s the connection is considered dead and reconnects.
 */

import { API_CONFIG } from '../config/api';

type EventHandler = (event: any) => void;

const BASE = API_CONFIG.baseURL;
const WS_URL = BASE.replace(/^http/, 'ws') + '/api/v1/ws';

export class TopnoteWS {
  private ws: WebSocket | null = null;
  private token: string;
  private handlers = new Set<EventHandler>();
  private connectHandlers = new Set<() => void>();
  private reconnectAttempts = 0;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;

  constructor(token: string) {
    this.token = token;
  }

  connect() {
    this.intentionallyClosed = false;
    this.open();
  }

  private open() {
    const url = `${WS_URL}?token=${encodeURIComponent(this.token)}`;
    this.ws = new WebSocket(url);

    this.ws.onmessage = (evt) => {
      let parsed: any = null;
      try {
        parsed = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (parsed.event === 'heartbeat') {
        this.resetHeartbeatTimer();
        return;
      }
      for (const h of this.handlers) {
        try {
          h(parsed);
        } catch {}
      }
    };

    this.ws.onopen = () => {
      console.log('[TopnoteWS] connected to', WS_URL);
      this.reconnectAttempts = 0;
      this.resetHeartbeatTimer();
      // Fire connect handlers on EVERY (re)connect. Events pushed while the
      // socket was down are lost (NOTIFY fan-out is fire-and-forget), so
      // consumers use this to refetch and catch up — otherwise a message
      // delivered during a disconnect only appears after a manual refresh.
      for (const h of this.connectHandlers) {
        try {
          h();
        } catch {}
      }
    };

    this.ws.onclose = () => {
      if (this.intentionallyClosed) return;
      console.warn(
        '[TopnoteWS] disconnected, reconnecting in',
        Math.min(30_000, 1_000 * 2 ** this.reconnectAttempts),
        'ms'
      );
      const delay = Math.min(30_000, 1_000 * 2 ** this.reconnectAttempts);
      this.reconnectAttempts += 1;
      setTimeout(() => this.open(), delay);
    };

    this.ws.onerror = (err) => {
      console.error('[TopnoteWS] error', err);
    };
  }

  private resetHeartbeatTimer() {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    // If no heartbeat in 60s, consider the connection dead
    this.heartbeatTimer = setTimeout(() => {
      try {
        this.ws?.close();
      } catch {}
    }, 60_000);
  }

  on(handler: EventHandler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  /** Register a handler fired on every (re)connect — use it to refetch/catch up. */
  onConnect(handler: () => void) {
    this.connectHandlers.add(handler);
    return () => this.connectHandlers.delete(handler);
  }

  close() {
    this.intentionallyClosed = true;
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    this.ws?.close();
  }
}
